import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { useInView } from "react-intersection-observer";

export enum CanvasBreakpoints {
  Sm = "sm",
  Md = "md",
  Lg = "lg",
  Xl = "xl",
  Xl2 = "xl2",
}

export const canvasWidthSizes: Record<CanvasBreakpoints, number> = {
  [CanvasBreakpoints.Sm]: 640,
  [CanvasBreakpoints.Md]: 768,
  [CanvasBreakpoints.Lg]: 1024,
  [CanvasBreakpoints.Xl]: 1280,
  [CanvasBreakpoints.Xl2]: 1536,
};

interface IParticle {
  radius: number;
  xPos: number;
  yPos: number;
  xVelocity: number;
  yVelocity: number;
  color: string;
}

export interface ICanvasParticles {
  width: number;
  height: number;
  colors?: string[];
  blurry?: boolean;
  border?: boolean;
  minRadius?: number;
  maxRadius?: number;
  minOpacity?: number;
  maxOpacity?: number;
  minSpeed?: number;
  maxSpeed?: number;
  numParticlesDesktop?: number;
  numParticlesMobile?: number;
  numParticlesDesktopRatio?: number; // This will be multiplied by the num of pixels (height)
  numParticlesMobileRatio?: number; // This will be multiplied by the num of pixels (height)
  widthBreakpoint?: CanvasBreakpoints;
  children?: React.ReactNode;
}

const CanvasParticles = ({
  height,
  width,
  colors = ["255, 255, 255", "255, 99, 71", "19, 19, 19"],
  blurry = false,
  border = false,
  minRadius = 10,
  maxRadius = 35,
  minOpacity = 0.05,
  maxOpacity = 0.2,
  minSpeed = 0.05,
  maxSpeed = 0.5,
  numParticlesDesktop,
  numParticlesMobile,
  numParticlesDesktopRatio = 0.03,
  numParticlesMobileRatio = 0.015,
  widthBreakpoint = CanvasBreakpoints.Md,
  children,
}: ICanvasParticles) => {
  const { ref, inView } = useInView({ initialInView: true });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvas, setCanvas] = useState<HTMLCanvasElement>();
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>();
  const [canvasWidth, setCanvasWidth] = useState<number>(width);
  const [canvasHeight, setCanvasHeight] = useState<number>(height);
  const [numParticles, setNumParticles] = useState<number>();

  useLayoutEffect(() => {
    setCanvasHeight(height);
    setCanvasWidth(width);

    createCircle();

  }, [height, width]);

  useEffect(() => {
    // Number of particles changes depending on screen size
    setNumParticles(
      width > canvasWidthSizes[widthBreakpoint]
        ? numParticlesDesktop ?? numParticlesDesktopRatio * height
        : numParticlesMobile ?? numParticlesMobileRatio * height
    );
  }, [width, height]);

  useEffect(() => {
    if (canvasRef && canvasRef.current) {
      setCanvas(canvasRef.current);
      setCtx(canvasRef.current.getContext("2d"));
    }
  }, []);

  useEffect(() => {
    if (canvas) createCircle();
  }, [canvas]);

  /**
   * generates random number between min and max values
   * @param  {number} min value
   * @param  {number} max malue
   * @return {number} random number between min and max
   * @method _rand
   */
  const _rand = (min: number, max: number) => {
    return Math.random() * (max - min) + min;
  };

  /**
   * Draws particles on canvas
   * @param  {array} particle array from createCircle method
   * @param  {number} i value from createCircle method
   * @method draw
   */
  const draw = (particle: Array<IParticle>, i: number) => {
    if (!canvas || !ctx) return;

    if (blurry === true) {
      //creates gradient if blurry === true
      const grd = ctx.createRadialGradient(
        particle[i].xPos,
        particle[i].yPos,
        particle[i].radius,
        particle[i].xPos,
        particle[i].yPos,
        particle[i].radius / 1.25
      );

      grd.addColorStop(1.0, particle[i].color);
      grd.addColorStop(0.0, "rgba(34, 34, 34, 0)");
      ctx.fillStyle = grd;
    } else {
      //otherwise sets to solid color w/ opacity value
      ctx.fillStyle = particle[i].color;
    }

    if (border === true) {
      ctx.strokeStyle = "#fff";
      ctx.stroke();
    }

    ctx.beginPath();
    ctx.arc(
      particle[i].xPos,
      particle[i].yPos,
      particle[i].radius,
      0,
      2 * Math.PI,
      false
    );
    ctx.fill();
  };

  /**
   * Resets position of particle when it goes off screen
   * @param  {array} particle value from createCircle & draw methods
   * @param  {number} i value from createCircle method
   * @method resetParticle
   */
  const resetParticle = (particle: Array<IParticle>, i: number) => {
    if (!canvas || !ctx) return;

    const random = _rand(0, 1);

    if (random > 0.5) {
      // 50% chance particle comes from left side of window...
      particle[i].xPos = -particle[i].radius;
      particle[i].yPos = _rand(0, canvas.height);
    } else {
      //... or bottom of window
      particle[i].xPos = _rand(0, canvas.width);
      particle[i].yPos = canvas.height + particle[i].radius;
    }
    //redraw particle with new values
    draw(particle, i);
  };

  /**
   * Clears canvas between animation frames
   * @method clearCanvas
   */
  const clearCanvas = () => {
    if (!canvas || !ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  /**
   * Animates particles
   * @param  {array} particle value from createCircle & draw methods
   * @method animate
   */
  const animate = (particle: Array<IParticle>) => {
    if (!canvas || !ctx || !numParticles) return;

    const animation = () => {
      //clears canvas
      clearCanvas();
      //then redraws particles in new positions based on velocity
      for (let i = 0; i < numParticles; i++) {
        particle[i].xPos += particle[i].xVelocity;
        particle[i].yPos -= particle[i].yVelocity;

        //if particle goes off screen call reset method to place it offscreen to the left/bottom
        if (
          particle[i].xPos > canvasWidth + particle[i].radius ||
          particle[i].yPos > canvasHeight + particle[i].radius
        ) {
          resetParticle(particle, i);
        } else {
          draw(particle, i);
        }
      }

      window.requestAnimationFrame(animation);
    };

    window.requestAnimationFrame(animation);
  };

  /**
   * Randomly creates particle attributes
   * @method createCircle
   */
  const createCircle = () => {
    if (!canvas || !ctx || !numParticles) return;

    const particle = [];

    for (let i = 0; i < numParticles; i++) {
      const color = colors[~~_rand(0, colors.length)];

      particle[i] = {
        radius: _rand(minRadius, maxRadius),
        xPos: _rand(0, canvasWidth),
        yPos: _rand(0, canvasHeight),
        xVelocity: _rand(minSpeed, maxSpeed),
        yVelocity: _rand(minSpeed, maxSpeed),
        color: "rgba(" + color + "," + _rand(minOpacity, maxOpacity) + ")",
      };

      //once values are determined, draw particle on canvas
      draw(particle, i);
    }
    //...and once drawn, animate the particle
    animate(particle);
  };

  return (
    <div ref={ref}>
      {canvasHeight && canvasWidth ? (
        <canvas
          ref={canvasRef}
          height={canvasHeight}
          width={canvasWidth}
          className={`relative`}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default CanvasParticles;
